import React, { useMemo } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import { ConsumerVoiceAudit } from '../../types/consumerVoice';

// Consumer Voice Score over time for one subject — the CV counterpart of
// PdpScoreHistory's chart. Fed the same rows the history table shows, so
// there is no extra request: ready runs with a score, oldest first. Renders
// nothing below two points (one run is the table's job to show).

const SERIES = [
  { key: 'overall', label: 'Overall', color: 'var(--accent-text)' },
  { key: 'sentiment', label: 'Sentiment', color: '#1C7A5A' },
  { key: 'advocacy', label: 'Advocacy', color: '#2474DD' },
] as const;

export function ConsumerVoiceScoreChart({ rows }: { rows: ConsumerVoiceAudit[] }) {
  const points = useMemo(
    () =>
      rows
        .filter((r) => r.status === 'ready' && r.overall_score != null && (r.scored_at || r.created_at))
        .map((r) => ({
          at: new Date(r.scored_at || r.created_at),
          overall: r.overall_score as number,
          sentiment: r.subscores?.sentiment ?? null,
          advocacy: r.subscores?.advocacy ?? null,
        }))
        .sort((a, b) => a.at.getTime() - b.at.getTime()),
    [rows],
  );

  if (points.length < 2) return null;

  return (
    <div className="border border-border-token rounded-[11px] bg-surface-2 px-4 py-3 mb-4">
      <div className="text-[12px] font-semibold text-text-2 mb-1">Score over time</div>
      <LineChart
        height={220}
        xAxis={[
          {
            scaleType: 'time',
            data: points.map((p) => p.at),
            valueFormatter: (d: Date) => dayjs(d).format('MMM DD'),
          },
        ]}
        yAxis={[{ min: 0, max: 100 }]}
        series={SERIES.map((s) => ({
          label: s.label,
          color: s.color,
          curve: 'monotoneX' as const,
          showMark: true,
          // A run with no sub-scores leaves a gap rather than a zero.
          connectNulls: true,
          data: points.map((p) => p[s.key]),
        }))}
        margin={{ top: 40, right: 16, bottom: 24, left: 36 }}
      />
    </div>
  );
}
